import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  useJsApiLoader,
  GoogleMap,
  OverlayViewF
} from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { GoogleMapContainer, HiddenZeroDiv } from './styled';
import { MapMarkerOverlay } from './MapMarkerOverlay';
import { getGoogleApiKey } from '../../../utils/helpers/common';
import useScreenSize from '../../../hooks/useScreenSize';
import BinaModal from '../../ReactModal';
import { PashaProjectModal } from './MobileProjectsModal';
import { calculateOverlayStackIndexes } from '../../../utils/helpers/calculateOverlayStackIndexes';
import type { ObjectItem, MapProps } from '../types';
import { useOnScreen } from '../../../hooks/useOnScreen';
import Loader from '../../Loader';
import { getLangDefinition } from '../../../utils/helpers';
import { SectionHeader } from '../SectionHeader';

const MAP_PADDING = { top: 20, bottom: 20, left: 120, right: 120 };

export const ObjectMapGoogle: React.FC<MapProps> = ({
  data,
  setLocationsRef,
  setVisibleTabRef
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [stackIndexes, setStackIndexes] = useState<{ [idx: number]: number }>(
    {}
  );
  const [selectedItem, setSelectedItem] = useState<ObjectItem | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [fitDone, setFitDone] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const mapTitleRef = useRef(null);
  const isVisible = useOnScreen(mapTitleRef);

  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleApiKey(),
    language: getLangDefinition(language),
    region: 'AZ'
  });
  const { isSmall, isTablet } = useScreenSize();

  const MAP_HEIGHT = isSmall ? 600 : 700;

  const containerStyle = {
    width: '100%',
    height: `${MAP_HEIGHT}px`
  };

  const sortedPlacements = useMemo(
    () => [...data.placements].sort((a, b) => b.lat - a.lat),
    [data.placements]
  );

  const handleOverlayViewLoad = (
    overlay: google.maps.OverlayView | null
  ): void => {
    overlayRef.current = overlay;
    if (!overlay) return;
    const projection = overlay.getProjection && overlay.getProjection();
    if (!projection) return;

    const stack = calculateOverlayStackIndexes(sortedPlacements, projection);
    setStackIndexes(stack);
  };

  const handleMapIdle = () => {
    if (fitDone) return;
    if (!mapRef.current) return;
    if (!data.placements.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    data.placements.forEach(item => {
      bounds.extend({ lat: item.lat, lng: item.lng });
    });
    try {
      mapRef.current.fitBounds(bounds, MAP_PADDING);
    } catch {
      mapRef.current.fitBounds(bounds, MAP_PADDING);
    }
    setFitDone(true);
  };

  useEffect(() => {
    setFitDone(false);
  }, [data.placements]);

  useEffect(() => {
    setLocationsRef?.(mapTitleRef);
    if (isVisible) return setVisibleTabRef?.(mapTitleRef);
  }, [isVisible]);

  const mapOptions =
    isLoaded && window.google
      ? {
          gestureHandling: isSmall ? 'greedy' : 'auto',
          disableDefaultUI: true,
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_CENTER
          },
          clickableIcons: false
        }
      : {};

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    return undefined;
  };

  const getCenterFromPlacements = (placements: ObjectItem[]) => {
    if (!placements.length) return { lat: 40.409264, lng: 49.867092 };
    const lat =
      placements.reduce((sum, p) => sum + p.lat, 0) / placements.length;
    const lng =
      placements.reduce((sum, p) => sum + p.lng, 0) / placements.length;
    return { lat, lng };
  };

  const center = useMemo(
    () => getCenterFromPlacements(data.placements),
    [data.placements]
  );

  if (!isLoaded) return <Loader loading={true} optionalClass="map-loader" />;

  return (
    <div ref={mapTitleRef} data-cy="pasha-map">
      <SectionHeader title={t('pasha.top_bar_locations')} />
      <GoogleMapContainer>
        <GoogleMap
          mapContainerStyle={containerStyle}
          options={mapOptions}
          onLoad={onMapLoad}
          onIdle={handleMapIdle}
        >
          <OverlayViewF
            position={center}
            mapPaneName="overlayMouseTarget"
            onLoad={handleOverlayViewLoad}
          >
            <HiddenZeroDiv />
          </OverlayViewF>
          {sortedPlacements.map((item: ObjectItem, idx: number) => {
            const stackIndex = stackIndexes[idx] || 0;
            if (stackIndex > 1) return null;
            return (
              <OverlayViewF
                key={idx}
                position={{ lat: item.lat, lng: item.lng }}
                mapPaneName="overlayMouseTarget"
              >
                <MapMarkerOverlay
                  item={item}
                  idx={idx}
                  stackIndex={stackIndex}
                  hoveredIdx={hoveredIdx}
                  setHoveredIdx={setHoveredIdx}
                  showModal={isSmall || isTablet}
                  isModalOpen={isModalOpen}
                  setSelectedItem={setSelectedItem}
                  setModalOpen={setModalOpen}
                  t={t}
                />
              </OverlayViewF>
            );
          })}
        </GoogleMap>
      </GoogleMapContainer>
      {(isSmall || isTablet) && selectedItem && isModalOpen && (
        <BinaModal
          isOpen={isModalOpen}
          onRequestClose={() => setModalOpen(false)}
          overlayClassName="modal__overlay--transparent"
          contentClassName="modal__content-pasha"
        >
          <PashaProjectModal
            name={selectedItem.name}
            address={selectedItem.address}
            coverUrl={selectedItem.coverUrl}
            url={selectedItem.path || selectedItem.url}
            onClose={() => setModalOpen(false)}
          />
        </BinaModal>
      )}
    </div>
  );
};
